import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Button } from '@/components/ui';
import { IAccountingUser } from '@/interfaces';
import { MoreHorizontal, Eye, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { broadcastService } from '@/services';
import { useUserConnected } from '@/hooks';
import { TEditParams } from '@/hooks/forms/useFormAccountingUser';
import { TCallbackForm } from '@/types';

interface TProps {
    accountingUser: IAccountingUser;
    onDelete: (_id: string) => Promise<void>;
    onEdit: (params: TEditParams) => void;
    onSeeDetails: (accountingUser: IAccountingUser, callback?: TCallbackForm<IAccountingUser>) => void;
}

const ConfirmDelete = ({ onConfirm, onCancel, name }: { onConfirm: () => void, onCancel: () => void, name: string }) => {
    return (
        <div className='flex flex-col items-center text-center space-y-4 py-2'>
            <div className='h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center'>
                <AlertTriangle className='h-6 w-6 text-destructive' />
            </div>
            <div className='space-y-1'>
                <p className='font-medium text-foreground'>Supprimer {name} ?</p>
                <p className='text-xs text-muted-foreground'>Cette action est irréversible.</p>
            </div>
            <div className='flex space-x-2 pt-2 w-full'>
                <Button
                    variant='outline'
                    onClick={onCancel}
                    size='sm'
                    className='flex-1'
                >
                    Annuler
                </Button>
                <Button
                    variant='destructive'
                    onClick={onConfirm}
                    size='sm'
                    className='flex-1'
                >
                    Supprimer
                </Button>
            </div>
        </div>
    );
};

const DataTableBodyCellOptions = ({ accountingUser, onDelete, onEdit, onSeeDetails }: TProps) => {

    const { userConnected } = useUserConnected();
    const isRoot = userConnected?.root === true;

    const handleOnClickSeeDetails = () =>
        setTimeout(() => onSeeDetails(accountingUser), 50);

    const handleOnClickEdit = () =>
        setTimeout(() => onEdit({ accountingUser }), 50);

    const handleOnClickDelete = () =>
        setTimeout(() => {
            broadcastService.modal.show({
                content: <ConfirmDelete
                    name={accountingUser.name}
                    onConfirm={() => {
                        // Close modal immediately (optimistic)
                        broadcastService.modal.hide();
                        // Trigger delete (will update cache optimistically)
                        onDelete(accountingUser._id!);
                    }}
                    onCancel={() => broadcastService.modal.hide()}
                />,
                width: 'xs',
                showCloseIcon: false
            });
        }, 50);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal size={16} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
                <DropdownMenuItem onClick={handleOnClickSeeDetails} className="cursor-pointer">
                    <Eye className='mr-2 h-4 w-4' />
                    Voir les détails
                </DropdownMenuItem>
                {isRoot && (
                    <DropdownMenuItem onClick={handleOnClickEdit} className="cursor-pointer">
                        <Pencil className='mr-2 h-4 w-4' />
                        Modifier
                    </DropdownMenuItem>
                )}
                {isRoot && (
                    <DropdownMenuItem onClick={handleOnClickDelete} className='text-destructive cursor-pointer'>
                        <Trash2 className='mr-2 h-4 w-4' />
                        Supprimer
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );

};

export default DataTableBodyCellOptions;

